import React, { useState, useRef, useEffect } from 'react'
import { PropertyHeaderProps } from './PropertyHeader.types'
import {
  HeaderContainer,
  HeaderContent,
  PropertyInfo,
  // RadarContainer,
  PropertyDetails,
  PropertyTitle,
  EditableTitle,
  PropertyMeta,
  MetaItem,
  PropertyTags,
  PropertyActions,
  ActionGroup,
  HeaderActions,
  RadarChartContainer,
  ActionButtonLabel,
  ActionButtonCount,
  ActionBtn,
  PrimaryActionBtn,
  PrimaryActionCount,
} from './PropertyHeader.styles'
import { Tag } from '../../atoms/Tag'
import { MetaDivider } from '../../atoms/MetaDivider'
import { BuildingPopetyIcon } from '../BuildingPopetyIcon'

export const PropertyHeader: React.FC<PropertyHeaderProps> = ({
  title,
  onTitleChange,
  icon,
  popetyClassification,
  metadata = {},
  tags = [],
  actions = [],
  // radarData,
  primaryAction,
  customPrimaryAction,
  secondaryAction,
  extraAction,
  tertiaryAction,
  className,
}) => {
  const metaItems = Object.entries(metadata).filter(([_, value]) => value)
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(title)
  const inputRef = useRef<HTMLInputElement>(null)

  // Update editValue when title prop changes
  useEffect(() => {
    setEditValue(title)
  }, [title])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleTitleClick = () => {
    if (onTitleChange) {
      setIsEditing(true)
    }
  }

  const handleSave = () => {
    const trimmedValue = editValue.trim()
    if (trimmedValue && trimmedValue !== title && onTitleChange) {
      onTitleChange(trimmedValue)
    } else {
      setEditValue(title) // Reset if empty or unchanged
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      setEditValue(title)
      setIsEditing(false)
    }
  }

  return (
    <HeaderContainer className={className}>
      <HeaderContent>
        <PropertyInfo>
          <RadarChartContainer>
            {icon || (
              <BuildingPopetyIcon
                classification={popetyClassification as any}
              />
            )}
          </RadarChartContainer>
          <PropertyDetails>
            {isEditing ? (
              <EditableTitle
                ref={inputRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
              />
            ) : (
              <PropertyTitle
                $editable={!!onTitleChange}
                onClick={handleTitleClick}
              >
                {title}
              </PropertyTitle>
            )}
            {metaItems.length > 0 && (
              <PropertyMeta>
                {metaItems.map(([key, value], index) => (
                  <React.Fragment key={key}>
                    <MetaItem>{value}</MetaItem>
                    {index < metaItems.length - 1 && <MetaDivider />}
                  </React.Fragment>
                ))}
              </PropertyMeta>
            )}
            {tags.length > 0 && (
              <PropertyTags>
                {tags.map((tag, index) => (
                  <Tag
                    key={index}
                    variant={tag.variant || 'gray'}
                    icon={tag.icon}
                  >
                    {tag.label}
                  </Tag>
                ))}
              </PropertyTags>
            )}
          </PropertyDetails>
        </PropertyInfo>

        <PropertyActions>
          {actions.length > 0 && (
            <ActionGroup>
              {actions.map((action, index) => (
                <ActionBtn
                  key={index}
                  variant="secondary"
                  onClick={action.onClick}
                  size="small"
                  $active={action.active}
                >
                  {action.icon}
                  <ActionButtonLabel>{action.label}</ActionButtonLabel>
                  {action.count !== undefined && (
                    <ActionButtonCount>{action.count}</ActionButtonCount>
                  )}
                </ActionBtn>
              ))}
            </ActionGroup>
          )}

          <HeaderActions>
            {secondaryAction && (
              <PrimaryActionBtn
                variant="secondary"
                size="small"
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.icon}
                <ActionButtonLabel>{secondaryAction.label}</ActionButtonLabel>
              </PrimaryActionBtn>
            )}
            {customPrimaryAction
              ? customPrimaryAction
              : primaryAction && (
                  <PrimaryActionBtn
                    variant="primary"
                    size="small"
                    onClick={primaryAction.onClick}
                    disabled={primaryAction.disabled}
                    title={primaryAction.title}
                  >
                    {primaryAction.icon}
                    <ActionButtonLabel>{primaryAction.label}</ActionButtonLabel>
                    {primaryAction.count !== undefined &&
                      primaryAction.count > 0 && (
                        <PrimaryActionCount>
                          {primaryAction.count}
                        </PrimaryActionCount>
                      )}
                  </PrimaryActionBtn>
                )}
            {tertiaryAction && (
              <PrimaryActionBtn
                variant="primary"
                size="small"
                onClick={tertiaryAction.onClick}
                disabled={tertiaryAction.disabled}
                loading={tertiaryAction.loading}
              >
                {tertiaryAction.icon}
                <ActionButtonLabel>{tertiaryAction.label}</ActionButtonLabel>
                {tertiaryAction.count !== undefined &&
                  tertiaryAction.count > 0 && (
                    <PrimaryActionCount>
                      {tertiaryAction.count}
                    </PrimaryActionCount>
                  )}
              </PrimaryActionBtn>
            )}
            {extraAction}
          </HeaderActions>
        </PropertyActions>
      </HeaderContent>
    </HeaderContainer>
  )
}
